import styles from './Table.module.scss';

import { useEventCallback } from '@mui/material/utils';
import { Column, ColumnFilter } from '@tanstack/react-table';
import clsx from 'clsx';
import i18next from 'i18next';
import { type JSX, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnInstance } from 'react-table';

import { AppointmentStatePicker } from '@work4all/components/lib/components/entity-picker/appointmentStatePicker/AppointmentStatePicker';
import { ArticleKindPicker } from '@work4all/components/lib/components/entity-picker/article-kind-picker/ArticleKindPicker';
import { BillableStatusPicker } from '@work4all/components/lib/components/entity-picker/billable-status-picker/BillableStatusPicker';
import { BooleanPicker } from '@work4all/components/lib/components/entity-picker/boolean-picker/BooleanPicker';
import { ClosedStatusPicker } from '@work4all/components/lib/components/entity-picker/ClosedStatusPicker/ClosedStatusPicker';
import { IFilterPickerConfig } from '@work4all/components/lib/components/entity-picker/components/list-entity-picker';
import { DueDateClassifiedPicker } from '@work4all/components/lib/components/entity-picker/due-date-classified-picker/DueDateClassifiedPicker';
import { EInvoiceFormatPicker } from '@work4all/components/lib/components/entity-picker/e-invoice-format-picker/EInvoiceFormatPicker';
import { EmailKindPicker } from '@work4all/components/lib/components/entity-picker/email-kind-picker/EmailKindPicker';
import { PrimitivePicker } from '@work4all/components/lib/components/entity-picker/enum-picker/PrimitivePicker';
import { InvoiceFormPicker } from '@work4all/components/lib/components/entity-picker/invoice-form-picker/InvoiceFormPicker';
import { InvoiceKindPicker } from '@work4all/components/lib/components/entity-picker/invoice-kind-picker/InvoiceKindPicker';
import { LedgerAccountKindPicker } from '@work4all/components/lib/components/entity-picker/ledger-account-kind-picker/LedgerAccountKindPicker';
import { ObjectTypePicker } from '@work4all/components/lib/components/entity-picker/object-type-picker/ObjectTypePicker';
import { PaymentStatusPicker } from '@work4all/components/lib/components/entity-picker/payment-status-picker/PaymentStatusPicker';
import { PermitStatusPicker } from '@work4all/components/lib/components/entity-picker/permit-status-picker/PermitStatusPicker';
import { PriorityPicker } from '@work4all/components/lib/components/entity-picker/PriorityPicker';
import { ProjectTrafficLightStatusPicker } from '@work4all/components/lib/components/entity-picker/project-traffic-light-status-picker/ProjectTrafficLightStatusPicker';
import { RaAccountingStatusPicker } from '@work4all/components/lib/components/entity-picker/ra-accounting-status-picker/RaAccountingStatusPicker';
import { ReAccountingStatusPicker } from '@work4all/components/lib/components/entity-picker/re-accounting-status-picker/ReAccountingStatusPicker';
import { RythmStatusPicker } from '@work4all/components/lib/components/entity-picker/rythm-picker/RythmStatusPicker';
import { SalesOpportunitiesStatusPicker } from '@work4all/components/lib/components/entity-picker/sales-opportunities-status-picker/SalesOpportunitiesStatusPicker';
import { TaskPrioPicker } from '@work4all/components/lib/components/entity-picker/task-prio-picker/TaskPrioPicker';
import { TaskStatusPicker } from '@work4all/components/lib/components/entity-picker/task-status-picker/TaskStatusPicker';
import { TicketStatusPicker } from '@work4all/components/lib/components/entity-picker/ticket-status-picker/TicketStatusPicker';
import { TravelReceiptsStatusPicker } from '@work4all/components/lib/components/entity-picker/travel-receipts-status-picker/TravelReceiptsStatusPicker';
import { VacationKindPicker } from '@work4all/components/lib/components/entity-picker/vacation-kind-picker/VacationKindPicker';
import { VacationStatusPicker } from '@work4all/components/lib/components/entity-picker/vacation-status-picker/VacationStatusPicker';
import {
  ColumnAdditionalData,
  FilterType,
  useTableState,
} from '@work4all/components/lib/dataDisplay/basic-table';
import {
  DateRangePicker,
  getTimeSpanUnit,
} from '@work4all/components/lib/dataDisplay/basic-table/components/date-range-picker/DateRangePicker';
import { NumberRangePicker } from '@work4all/components/lib/dataDisplay/basic-table/components/number-range-picker/NumberRangePicker';
import { RequiredTimeRangePicker } from '@work4all/components/lib/dataDisplay/basic-table/components/required-time-range-picker/RequiredTimeRangePicker';
import { SearchInput } from '@work4all/components/lib/dataDisplay/basic-table/components/search-input/SearchInput';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { IndividualFieldControlType } from '@work4all/models/lib/Enums/IndividualFieldControlType.enum';
import { UserClass } from '@work4all/models/lib/Enums/UserClass.enum';

import { ListPageContext } from '../../../containers/file-entities-lists/list-page-context';
import { settings, useSetting } from '../../../settings';
import { PopoverFilterWrap } from '../../filters/popover-filter-wrap/PopoverFilterWrap';
import { SalesOpportunitiesGroupPicker } from '../../filters/SalesOpportunitiesGroupPicker';

import { ChronoContactPicker } from './components/chrono-contact-picker/ChronoContactPicker';
import { getConditionalPicker } from './getConditionalPicker';
import { StateFullPickerWrap } from './StateFullPickerWrap';

interface RenderPickerOptions {
  columnDef: ColumnAdditionalData;
  filterValue: any;
  setFilterValue: (value: any) => void;
}

function renderPicker({
  columnDef,
  filterValue,
  setFilterValue,
}: RenderPickerOptions) {
  let Picker = null;
  switch (columnDef.filterType) {
    case FilterType.EmailKind:
      Picker = EmailKindPicker;
      break;
    case FilterType.TicketStatus:
      Picker = TicketStatusPicker;
      break;
    case FilterType.AppointmentState:
      Picker = AppointmentStatePicker;
      break;
    case FilterType.TaskStatus:
      Picker = TaskStatusPicker;
      break;
    case FilterType.EInvoiceFormat:
      Picker = EInvoiceFormatPicker;
      break;
    case FilterType.VacationKind:
      Picker = VacationKindPicker;
      break;
    case FilterType.ObjectType:
      Picker = ObjectTypePicker;
      break;
    case FilterType.ChronoContactPicker:
      Picker = ChronoContactPicker;
      break;
    case FilterType.SalesOpportunitiesGroupPicker:
      Picker = SalesOpportunitiesGroupPicker;
      break;
    case FilterType.SalesOpportunitiesStatusPicker:
      Picker = SalesOpportunitiesStatusPicker;
      break;
    case FilterType.TravelReceiptStatus:
      Picker = TravelReceiptsStatusPicker;
      break;
    case FilterType.PaymentStatus:
      Picker = PaymentStatusPicker;
      break;
    case FilterType.InvoiceForm:
      Picker = InvoiceFormPicker;
      break;
    case FilterType.ReAccountingStatus:
      Picker = ReAccountingStatusPicker;
      break;
    case FilterType.RaAccountingStatus:
      Picker = RaAccountingStatusPicker;
      break;
    case FilterType.PermitStatus:
      Picker = PermitStatusPicker;
      break;
    case FilterType.VacationStatus:
      Picker = VacationStatusPicker;
      break;
    case FilterType.InvoiceKind:
      Picker = InvoiceKindPicker;
      break;
    case FilterType.LedgerAccountKind:
      Picker = LedgerAccountKindPicker;
      break;
    case FilterType.DueDateClassified:
      Picker = DueDateClassifiedPicker;
      break;
    case FilterType.ArticleKind:
      Picker = ArticleKindPicker;
      break;
    case FilterType.RythmStatus:
      Picker = RythmStatusPicker;
      break;
    case FilterType.TaskPriority:
      Picker = TaskPrioPicker;
      break;
    case FilterType.TicketPriority:
      Picker = PriorityPicker;
      break;
    case FilterType.ProjectTrafficLightStatus:
      Picker = ProjectTrafficLightStatusPicker;
      break;
    default:
      break;
  }
  return !Picker ? null : (
    <Picker
      multiple={true}
      value={filterValue?.value}
      onChange={(value) => {
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          setFilterValue(null);
        } else {
          setFilterValue({
            value: value,
            filterType: columnDef.filterType,
          });
        }
      }}
    />
  );
}

interface TableFilterInnerProps {
  title: string;
  columnId: string;
  columnDef: ColumnAdditionalData;
  filterValue: any;
  setFilterValue: (value: any) => void;
  listEntityType?: Entities;
  tableFilters: any;
  children?: JSX.Element;
}

interface TableFilterV7Props {
  column: ColumnInstance;
  children?: JSX.Element;
  listEntityType?: Entities;
}

function TableFilterV7(props: TableFilterV7Props) {
  const { column, listEntityType, children } = props;
  const tableState = useTableState();

  return (
    <TableFilterInner
      title={column.Header}
      columnId={column.id}
      filterValue={column.filterValue}
      setFilterValue={column.setFilter}
      columnDef={column}
      tableFilters={tableState?.filters}
      listEntityType={listEntityType}
      children={children}
    />
  );
}

interface TableFilterV8Props {
  column: Column<unknown, unknown>;
  children?: JSX.Element;
  listEntityType?: Entities;
  columnFilters: ColumnFilter[];
}

function TableFilterV8(props: TableFilterV8Props) {
  const { column, columnFilters, listEntityType, children } = props;

  return (
    <TableFilterInner
      title={column.columnDef.header as string}
      columnId={column.id}
      filterValue={column.getFilterValue()}
      setFilterValue={column.setFilterValue}
      columnDef={column.columnDef}
      tableFilters={columnFilters}
      listEntityType={listEntityType}
      children={children}
    />
  );
}

export { TableFilterV7 as TableFilter, TableFilterV8 };

const TableFilterInner: React.FC<TableFilterInnerProps> = (props) => {
  const {
    title,
    columnId,
    columnDef,
    filterValue,
    setFilterValue,
    tableFilters,
    listEntityType,
    children,
  } = props;

  const { t } = useTranslation();

  const listPageContext = useContext(ListPageContext);
  const entityType = listEntityType ?? listPageContext?.entityType;

  const closedEntitiesSetting = useSetting(
    settings.hideClosedEntities({ entityType })
  );

  const setHideClosedEntitiesSetting = useEventCallback((value: boolean) => {
    closedEntitiesSetting.set(value);
  });

  let comp;
  switch (columnDef.filterType) {
    case FilterType.Date:
      comp = (
        <DateRangePicker
          startDate={
            filterValue?.value?.startDate ? filterValue?.value.startDate : null
          }
          endDate={
            filterValue?.value?.endDate ? filterValue?.value.endDate : null
          }
          onChange={(startDate, endDate) => {
            if (startDate === null && endDate === null) {
              setFilterValue(null);
            } else {
              setFilterValue({
                value: {
                  startDate: startDate ? startDate : undefined,
                  endDate: endDate ? endDate : undefined,
                  span: getTimeSpanUnit(startDate, endDate),
                },
                filterType: columnDef.filterType,
              });
            }
          }}
          {...columnDef.filterParams}
        />
      );
      break;

    case FilterType.Number:
      comp = (
        <NumberRangePicker
          from={filterValue?.value?.from ? filterValue?.value.from : null}
          to={filterValue?.value?.to ? filterValue?.value.to : null}
          onChange={(from, to) => {
            if (from === null && to === null) {
              setFilterValue(null);
            } else {
              setFilterValue({
                value: {
                  from: from ? from : null,
                  to: to ? to : null,
                },
                filterType: columnDef.filterType,
              });
            }
          }}
        />
      );
      break;

    case FilterType.ClosedStatus:
      comp = (
        <ClosedStatusPicker
          trueText={t(`${entityType?.toUpperCase()}.CLOSED_STATUS.HIDE_CLOSED`)}
          falseText={t(`${entityType?.toUpperCase()}.CLOSED_STATUS.SHOW_ALL`)}
          value={filterValue?.value ?? false}
          onChange={(value) => {
            setFilterValue({
              value,
              filterType: columnDef.filterType,
            });

            setHideClosedEntitiesSetting(value);
          }}
        />
      );
      break;
    case FilterType.BooleanNumber:
    case FilterType.Boolean:
      comp = (
        <BooleanPicker
          multiple={true}
          value={filterValue?.value}
          onChange={(value) => {
            if (
              value === null ||
              (Array.isArray(value) && value.length === 0)
            ) {
              setFilterValue(null);
            } else {
              setFilterValue({
                value: value,
                filterType: columnDef.filterType,
              });
            }
          }}
        />
      );
      break;

    case FilterType.Search:
      comp = (
        <SearchInput
          value={filterValue?.value ?? ''}
          onChange={(value) => {
            const normalizedValue = value.trim().length > 0 ? value : null;

            // If the actual search string didn't change, don't update the
            // filter to avoid unnesessary re-renders.
            if (
              (filterValue == null && normalizedValue === null) ||
              filterValue?.value === normalizedValue
            ) {
              return;
            }

            setFilterValue(
              normalizedValue !== null
                ? {
                    value,
                    filterType: columnDef.filterType,
                  }
                : null
            );
          }}
        />
      );
      break;
    case FilterType.Primitive: {
      const isCustomField =
        columnDef.filterParams?.entity === Entities.customField;
      const filterPickerConfig: IFilterPickerConfig | null =
        (!listEntityType && listPageContext === null) || isCustomField
          ? null
          : {
              parentEntityType: listEntityType ?? listPageContext.entityType,
              field: columnId,
              filter: null,
            };
      comp = (
        <StateFullPickerWrap
          columnFilters={tableFilters}
          stateLessCfg={filterPickerConfig}
          render={(filterPickerConfig: IFilterPickerConfig) => {
            return (
              <PrimitivePicker
                value={filterValue?.value ?? ''}
                filterConfig={getFilterConfig(filterPickerConfig)}
                onChange={(value) => {
                  setFilterValue(
                    value.length
                      ? {
                          value,
                          filterType: columnDef.filterType,
                        }
                      : null
                  );
                }}
              />
            );
          }}
        />
      );
      break;
    }
    case FilterType.AppointmentBillable:
      comp = (
        <BillableStatusPicker
          multiple={true}
          value={filterValue?.value}
          onChange={(value) => {
            if (
              value === null ||
              (Array.isArray(value) && value.length === 0)
            ) {
              setFilterValue(null);
            } else {
              setFilterValue({
                value: value,
                filterType: columnDef.filterType,
              });
            }
          }}
        />
      );
      break;

    case FilterType.Picker: {
      const Picker = getConditionalPicker(
        columnDef.filterParams?.entity,
        columnDef.cellParams?.fieldType
      );

      if (!Picker) {
        comp = null;
        break;
      }

      const isCustomField =
        columnDef.filterParams?.entity === Entities.customField;
      const filterPickerConfig: IFilterPickerConfig | null =
        (!listEntityType && listPageContext === null) || isCustomField
          ? null
          : {
              parentEntityType: listEntityType ?? listPageContext.entityType,
              field: columnId,
              filter: null,
            };

      comp =
        Picker === null ? null : (
          <StateFullPickerWrap
            columnFilters={tableFilters}
            stateLessCfg={filterPickerConfig}
            render={(filterPickerConfig: IFilterPickerConfig) => {
              return (
                <Picker
                  {...(columnDef.filterParams?.entity === Entities.lookUp
                    ? { lookupType: columnDef.filterParams.lookupType }
                    : undefined)}
                  {...(isCustomField &&
                  columnDef.cellParams.fieldType ===
                    IndividualFieldControlType.COMBO
                    ? {
                        fieldOptions: columnDef.cellParams.fieldOptions,
                      }
                    : undefined)}
                  layout="advanced"
                  userClasses={[
                    UserClass.BENUTZER,
                    UserClass.MITARBEITER,
                    UserClass.RESSOURCE,
                  ]}
                  multiple={columnDef.filterParams?.multiple ?? true}
                  value={filterValue?.value}
                  filterConfig={getFilterConfig(filterPickerConfig)}
                  favorites={false}
                  onChange={(value) => {
                    if (
                      value === null ||
                      (Array.isArray(value) && value.length === 0)
                    ) {
                      setFilterValue(null);
                    } else {
                      setFilterValue({
                        value: value,
                        filterType: columnDef.filterType,
                      });
                    }
                  }}
                />
              );
            }}
          ></StateFullPickerWrap>
        );
      break;
    }
    case FilterType.Check: {
      comp = (
        <div style={{ padding: '0.5rem' }}>
          <CheckboxRadioItem
            label={i18next.t('FILTERS.CHECKED_ITEMS_ONLY')}
            checked={filterValue?.value === 'true'}
            onChange={(e) => {
              if (e.target.checked) {
                setFilterValue({
                  value: 'true',
                  filterType: columnDef.filterType,
                });
              } else {
                setFilterValue(null);
              }
            }}
          />
        </div>
      );
      break;
    }
    case FilterType.RequiredTime: {
      comp = (
        <RequiredTimeRangePicker
          from={filterValue?.value?.from ?? null}
          to={filterValue?.value?.to ?? null}
          onChange={(from, to) => {
            if (from === null && to === null) {
              setFilterValue(null);
            } else {
              setFilterValue({
                value: { from: from, to: to },
                filterType: columnDef.filterType,
              });
            }
          }}
          convertTo={columnDef.cellParams?.convertTo}
        />
      );
      break;
    }
    case FilterType.TravelReceiptStatus:
    case FilterType.EmailKind:
    case FilterType.VacationKind:
    case FilterType.EInvoiceFormat:
    case FilterType.TicketStatus:
    case FilterType.AppointmentState:
    // case FilterType.AppointmentBillable:
    case FilterType.ObjectType:
    case FilterType.ChronoContactPicker:
    case FilterType.SalesOpportunitiesGroupPicker:
    case FilterType.SalesOpportunitiesStatusPicker:
    case FilterType.PaymentStatus:
    case FilterType.InvoiceForm:
    case FilterType.ReAccountingStatus:
    case FilterType.RaAccountingStatus:
    case FilterType.PermitStatus:
    case FilterType.VacationStatus:
    case FilterType.InvoiceKind:
    case FilterType.LedgerAccountKind:
    case FilterType.DueDateClassified:
    case FilterType.ArticleKind:
    case FilterType.RythmStatus:
    case FilterType.TaskStatus:
    case FilterType.TicketPriority:
    case FilterType.TaskPriority:
    case FilterType.ProjectTrafficLightStatus: {
      comp = renderPicker({ columnDef, filterValue, setFilterValue });
      break;
    }
  }

  if (!comp && children) return children;
  /* 
  let customTitle;
  switch (columnDef.filterType) {
    case FilterType.AppointmentBillable:
      customTitle = t('APPOINTMENT.BILLABLE');
      break;
    default:
      customTitle = title;
      break;
  } */

  return (
    comp && (
      <PopoverFilterWrap
        disabled={filterValue?.readOnly}
        filterButtonClass={clsx(styles.filterButtonClass, {
          [styles.activeFilter]: !!filterValue,
          [styles.readOnly]: filterValue?.readOnly,
        })}
        title={title}
        customComponent={children}
      >
        <div className={styles['filter-wrapper']}>{comp}</div>
      </PopoverFilterWrap>
    )
  );
};

export function getFilterConfig(filterPickerConfig: IFilterPickerConfig) {
  if (!filterPickerConfig) return filterPickerConfig;
  // This list contains longer nested filters implemented by API
  const exeptionList = [
    'businessPartner.data.id',
    'businessPartnerContactCombined.contact.id',
    'businessPartnerContactCombined.businessPartner.data.id',
    'additionalAddress1.businessPartner.data.id',
    'additionalAddress1.contact.id',
    'additionalAddress2.businessPartner.data.id',
    'additionalAddress2.contact.id',
    'additionalAddress3.businessPartner.data.id',
    'additionalAddress3.contact.id',
  ];
  const result =
    filterPickerConfig.field.split('.').length <= 2 ||
    exeptionList.includes(filterPickerConfig.field)
      ? filterPickerConfig
      : undefined;

  const forcedAllEntries = ['bookings.projectId'];
  if (forcedAllEntries.includes(filterPickerConfig.field)) return undefined;

  if (!result)
    console.warn(
      `There is missing implementation for filter ${filterPickerConfig.field}`
    );
  return result;
}
